import _ from 'lodash';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import store from '@app/app.store';
import { getModuleArr, IModuleType, Module } from '@app/models';

interface IAppModuleContextState {
	module: IModuleType;
	modules: IModuleType[];
}

export interface IInjectedAppModuleProps {
	AppModule: IAppModuleContextState;
}

const AppModuleContext = createContext<IAppModuleContextState>({
	module: Module.Dashboard,
	modules: [],
});

const AppModuleProvider: React.FC = (props) => {
	const location = useLocation();
	const [allModules] = useState(getModuleArr);
	const [module, setModule] = useState<IModuleType>(Module.Dashboard);
	const [modules, setModules] = useState(() => {
		const accountLevel = store.getState()?.authentication?.user?.account?.level;
		const userRole = store.getState().authentication?.user?.role;
		const result = _.values(Module)
			.filter(
				(x) =>
					(x.location === 'header' || x.location === 'sidebar') &&
					x.level.includes(accountLevel) &&
					(x.userRoles === 'all' || x.userRoles.includes(userRole))
			)
			.sort((a, b) => a.sortOrder - b.sortOrder);

		return result;
	});

	useEffect(() => {
		const unsubscribe = store.subscribe(() =>
			setModules((prev) => {
				const accountLevel = store.getState()?.authentication?.user?.account.level;
				const userRole = store.getState()?.authentication?.user?.role;
				const result = _.values(Module)
					.filter(
						(x) =>
							(x.location === 'header' || x.location === 'sidebar') &&
							x.level.includes(accountLevel) &&
							(x.userRoles === 'all' || x.userRoles.includes(userRole))
					)
					.sort((a, b) => a.sortOrder - b.sortOrder);
				return result;
			})
		);

		return () => {
			unsubscribe();
		};
	}, [setModules, modules, store]);

	useEffect(() => {
		let currentModule = allModules.find(
			(x) => x.url === location.pathname || x.url === `/${location.pathname.split('/').slice(1)[0]}`
		);
		// for nested routes.
		if (!currentModule) {
			currentModule = allModules.find(
				(x) =>
					x.url &&
					location.pathname &&
					x.url.includes(':') &&
					x.url.slice(0, x.url.indexOf(':')) === location.pathname.slice(0, x.url.indexOf(':')) &&
					x.url.split('/').length === location.pathname.split('/').length
			);
		}

		if (currentModule) {
			setModule(currentModule);
		}
	}, [location]);

	const value = useMemo(
		() => ({
			module,
			modules,
		}),
		[module, modules]
	);

	return <AppModuleContext.Provider value={value}>{props.children}</AppModuleContext.Provider>;
};

/**
 * To use AppModule provider context in functional components
 * @returns AppModule context for functional components
 */
const useAppModule = () => useContext<IAppModuleContextState>(AppModuleContext);

/**
 * To use AppModule provider context in class based components
 * @param WrappedComponent Component which will use AppModule provider context
 * @returns WrappedComponent with AppModule provider props
 */
const withAppModule = <P extends IInjectedAppModuleProps>(
	WrappedComponent: React.ComponentType<P>
): React.ComponentType<Omit<P, keyof IInjectedAppModuleProps>> => {
	return (props) => (
		<AppModuleContext.Consumer>
			{(AppModule) => <WrappedComponent AppModule={AppModule} {...(props as P)} />}
		</AppModuleContext.Consumer>
	);
};

export { AppModuleProvider, useAppModule, withAppModule };



// WEBPACK FOOTER //
// ./src/providers/app-module.provider.tsx